import React from 'react';
import { useFormikContext } from 'formik';
import { Maths } from 'helpers/maths';
import FormGrid, { FormFieldConfig, FormRowConfig } from 'components/FormGrid';
import { COL_SPAN_HALF } from 'components/FormGrid/constants';
import FormInputWithPrecisionDisplay from 'components/PriceUnitField/components/FormInputWithPrecisionDisplay';
import { GroupedCurrencyPair, ReadOnlyCurrency } from './CurrencyDisplay';
import {
  parseControlledBlurValue,
  parseControlledChangeValue
} from '../helpers/parseFormValue';
import {
  FIXED_COST_FIELDS,
  calculateTotalFixedCost
} from '../helpers/fixedCostCalculations';
import { useStableFieldBuffer } from '../helpers/useStableFieldBuffer';
import { PlanWizardFormValues, WizardFixedCostValues } from '../../../types';

const DOLLAR_PREFIX = <span className="text-neutral-60">$</span>;
const TOTAL_SUFFIX = <span className="text-neutral-60">total</span>;
const PER_AC_SUFFIX = <span className="text-neutral-60">/ac</span>;

interface FixedCostSectionProps {
  area: number;
  financialFieldsDisabled: boolean;
}

const FixedCostSection: React.FC<FixedCostSectionProps> = ({
  area,
  financialFieldsDisabled
}) => {
  const { values, setFieldValue } = useFormikContext<PlanWizardFormValues>();
  const stableFieldObject = useStableFieldBuffer();

  const buildFixedCostFieldPair = (
    key: keyof WizardFixedCostValues,
    label: string
  ): FormFieldConfig[] => {
    const totalPath = `fixedCost.${key}`;
    const perAcKey = `${key}_PerAc` as keyof WizardFixedCostValues;
    const perAcPath = `fixedCost.${perAcKey}`;

    return [
      {
        id: totalPath,
        colSpan: COL_SPAN_HALF,
        render: () => (
          <GroupedCurrencyPair
            label={label}
            first={
              <FormInputWithPrecisionDisplay
                object={stableFieldObject(key, values.fixedCost[key])}
                fieldName={key}
                inputPrefix={DOLLAR_PREFIX}
                inputSuffix={TOTAL_SUFFIX}
                disabled={financialFieldsDisabled}
                allowEmptyDisplay
                onControlledChange={event => {
                  const parsed = parseControlledChangeValue(event);
                  setFieldValue(totalPath, parsed);
                  setFieldValue(
                    perAcPath,
                    parsed === null
                      ? null
                      : Maths.divideSafeRound(parsed, area, 2)
                  );
                }}
                onControlledBlur={newTotal => {
                  const parsed = parseControlledBlurValue(newTotal);
                  setFieldValue(totalPath, parsed);
                  setFieldValue(
                    perAcPath,
                    parsed === null
                      ? null
                      : Maths.divideSafeRound(parsed, area, 2)
                  );
                }}
              />
            }
            second={
              <FormInputWithPrecisionDisplay
                object={stableFieldObject(perAcKey, values.fixedCost[perAcKey])}
                fieldName={perAcKey}
                inputPrefix={DOLLAR_PREFIX}
                inputSuffix={PER_AC_SUFFIX}
                disabled={financialFieldsDisabled}
                allowEmptyDisplay
                onControlledChange={event => {
                  const parsed = parseControlledChangeValue(event);
                  setFieldValue(perAcPath, parsed);
                  setFieldValue(
                    totalPath,
                    parsed === null
                      ? null
                      : Maths.multiplyRound(parsed, area, 2)
                  );
                }}
                onControlledBlur={newPerAc => {
                  const parsed = parseControlledBlurValue(newPerAc);
                  setFieldValue(perAcPath, parsed);
                  setFieldValue(
                    totalPath,
                    parsed === null
                      ? null
                      : Maths.multiplyRound(parsed, area, 2)
                  );
                }}
              />
            }
          />
        )
      }
    ];
  };

  const totalFixedCost = calculateTotalFixedCost(values.fixedCost);

  // Total Fixed Cost fills the otherwise-empty last slot.
  const fixedCostRows: FormRowConfig[] = [];
  for (let i = 0; i < FIXED_COST_FIELDS.length; i += 2) {
    const first = FIXED_COST_FIELDS[i];
    const second = FIXED_COST_FIELDS[i + 1];
    fixedCostRows.push({
      id: `fixed-cost-row-${first.key}`,
      fields: [
        ...buildFixedCostFieldPair(first.key, first.label),
        ...(second
          ? buildFixedCostFieldPair(second.key, second.label)
          : [
              {
                id: 'totalFixedCost',
                colSpan: COL_SPAN_HALF,
                render: () => (
                  <ReadOnlyCurrency
                    label="Total Fixed Cost"
                    value={totalFixedCost}
                    testId="total-fixed-cost-value"
                  />
                )
              }
            ])
      ]
    });
  }

  return <FormGrid rows={fixedCostRows} />;
};

export default FixedCostSection;
